import { makeSelectors } from "../../core/make-selectors";
import { sortToc, tocById } from "./utils";
import { createSelector } from "@reduxjs/toolkit";
import { getCurrentCfi } from "../nav/selectors";
import {
  findIndexMatchingCfiFallBackToBefore,
  findIndexMatchingDocAndCfiFallBackToBefore,
  getCfiContentDocIdref,
  getExtendedCfi,
  safeParseCfi,
} from "../../../../../../common/utils/cfi";
import { removeDuplicates } from "../../../../../../common/utils/array";
import TocNode from "./TocNode";

export function getToc(state) {
  return selectors.getData(state);
}

export const selectors = makeSelectors<TocNode[]>("toc");

export const getTocNodesWithValidCFIs = createSelector(
  (state) => getToc(state),
  (tocData) => tocData.filter((entry) => !!entry.cfi)
);

export const getTocIdrefCfis = createSelector(getToc, (tocData) => {
  return tocData.reduce((result, node) => {
    const cfi = safeParseCfi(node.cfi);
    const idref = getCfiContentDocIdref(node.cfi);
    if (cfi && idref && !result[idref]) {
      result[idref] = cfi.contentDoc.toString();
    }
    return result;
  }, {});
});

// bear in mind these are influenced by MAX_TOC_DEPTH where lower levels
// are rolled up into higher levels to manage verbosity of toc depth
export const getSortedToc = createSelector(
  (state) => getToc(state),
  (tocData) => sortToc(tocData)
);
export const getTocNodeNames = createSelector(
  (state) => getSortedToc(state),
  (data) => getNodeNames(data)
);
export const getFlatToc = createSelector(
  (state) => getToc(state),
  (tocData) => tocById(tocData)
);
export const getActiveUuidParentList = createSelector(
  (state) => getActiveTocUuid(state),
  (state) => getFlatToc(state),
  (uuid, flatData) => {
    if (!uuid) {
      return [];
    }
    let result = [uuid];
    while (flatData[result[0]].parent) {
      result = [flatData[result[0]].parent, ...result];
    }
    result.pop();
    return result;
  }
);
// END WARNING

export const getTocStatus = selectors.getFetchStatus;

/**
 * Find the uuid associated with the toc entry that best represents the current
 * location in the book.
 * 1. Use a content doc match if at all possible
 * 2. If there is no content doc match, use the last entry in the doc before
 *    the current location
 * 3. If there are duplicate locations, only ever return the first one
 */
export const getActiveTocUuid = createSelector(
  (state) => getTocNodesWithValidCFIs(state),
  (state) => getCurrentCfi(state),
  (toc, cfi) => {
    const cleanedCfis = toc.map((tocItem) => {
      return { ...tocItem, cfi: getExtendedCfi(tocItem.cfi) };
    });
    const cleanedToc = removeDuplicates(cleanedCfis, (el) => el.cfi);
    let matchedIndex = findIndexMatchingDocAndCfiFallBackToBefore(
      cleanedToc,
      (el) => el.cfi,
      cfi
    );
    if (matchedIndex < 0) {
      matchedIndex = findIndexMatchingCfiFallBackToBefore(
        cleanedToc,
        (el) => el.cfi,
        cfi
      );
    }
    if (matchedIndex >= 0) {
      return cleanedToc[matchedIndex].uuid;
    }
  }
);

function iterateNodes(children) {
  let array: string[] = [];
  children.forEach((child) => {
    if (child.levelChildren) {
      array.push(child.uuid);
      let childrenArray = iterateNodes(child.levelChildren);
      return array.push(...childrenArray);
    } else {
      return array;
    }
  });
  return array;
}

function getNodeNames(results) {
  if (results.length > 0) {
    return iterateNodes(results);
  } else {
    return [];
  }
}

export const getTocTitleCount = createSelector(getToc, (toc) =>
  toc.reduce((result, t) => {
    result[t.title] = result[t.title] + 1 || 1;
    return result;
  }, {})
);
